import { CountingTextField } from "@lib/titan2/dist/game/control/CountingTextField";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { Back, gsap } from "gsap";
import { PixiPlugin } from "gsap/all";
import { SlingoEvent, TestEvent } from "../../../controller/event/SlingoEvent";
import { AbstractGameView } from "./AbstractGameView";
gsap.registerPlugin(PixiPlugin);
/**
 * WinDisplay.ts
 */
export class WinDisplay extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        if (!this.factory.useFlowStateMachine?.()) {
            if (this.model.isExpressGame()) {
                this.registerGame(SlingoEvent.SLINGO_ANIMATION, this.handleSlingo);
            }
            else {
                this.registerGame(SlingoEvent.SLINGO, this.handleSlingo);
            }
            this.registerGame(SlingoEvent.SLINGO_COMPLETE, this.handleSlingoComplete);
        }
        else {
            this.registerGame(SlingoEvent.SLINGO_WIN_ANIMATION, this.handleSlingo);
            this.registerGame(SlingoEvent.SLINGO_WIN_ANIMATION_COMPLETE, this.handleSlingoComplete);
        }
        this.registerGame(SlingoEvent.WIN_DISPLAY_START, this.handleStart);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.handlePurchaseSpinRecovery);
        this.registerGame(SlingoEvent.REELS_COMPLETE, this.handleReelsComplete);
        //this.registerGame(SlingoEvent.JOKER_WIN_ANIMATION, this.handleJokerWinAnimation);
        this.registerGame(TestEvent.SLINGO_1, this.handleTestSlingo1);
        this.registerGame(TestEvent.SLINGO_2, this.handleTestSlingo2);
        this.registerGame(TestEvent.SLINGO_3, this.handleTestSlingo3);
        this.registerGame(TestEvent.SLINGO_4, this.handleTestSlingo4);
        this.registerGame(TestEvent.GAME_SUMMARY, this.startSummary);
        this.registerGame(TestEvent.GAME_OVER, this.startGameOver);
    }
    create() {
        super.create();
        this.createWinBackground();
        this.createWinField();
        this.createWinLabelField();
        this.createSlingoAnimation();
        this.createSummary();
        this.createGameOver();
        this.createGameOverField();
        Logger.addObjectRef(this, "WinDisplay");
    }
    createWinBackground() {
        if (this.uiProperties.winBackgroundProperties) {
            this.winBackground = new TitanSprite(this.landscapeProperties.winBackgroundProperties, this.portraitProperties?.winBackgroundProperties);
        }
    }
    createWinField() {
        if (this.uiProperties.winFieldProperties) {
            this.winField = new CountingTextField(this.landscapeProperties.winFieldProperties, this.portraitProperties?.winFieldProperties, this.formatCurrency);
            this.winBackground.addChild(this.winField);
        }
    }
    createWinLabelField() {
        if (this.uiProperties.winLabelFieldProperties) {
            this.winLabelField = ViewUtils.createField(this, this.landscapeProperties.winLabelFieldProperties, this.portraitProperties?.winLabelFieldProperties);
        }
    }
    createSlingoAnimation() {
        this.slingoAnimationContainer = ViewUtils.createContainer(this, "slingoAnimationContainer");
        this.slingoAnimation = this.factory.createSlingoAnimation();
        this.slingoAnimation.onComplete(this.handleSlingoAnimationComplete, this);
        this.slingoAnimationContainer.addChild(this.slingoAnimation);
    }
    createSummary() {
        this.summary = this.factory.createSummary();
        this.summary.onComplete(this.handleGameComplete, this);
    }
    createGameOver() {
        this.gameOverContainer = ViewUtils.createContainer(this, "gameOverContainer");
        this.gameOver = new TitanSprite(this.landscapeProperties.gameOverProperties, this.portraitProperties?.gameOverProperties);
    }
    createGameOverField() {
        this.gameOverField = new TitanText(this.landscapeProperties.gameOverFieldProperties, this.portraitProperties?.gameOverFieldProperties);
        this.gameOver.addChild(this.gameOverField);
    }
    handlePurchaseEntry() {
        this.clearFields();
        this.updateTotalWin();
    }
    handleStandardSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handleFreeSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handlePurchaseSpinRecovery() {
        this.handlePurchaseEntry();
    }
    handleSlingoComplete() {
        this.updateTotalWin();
    }
    handleJokerWinAnimation() {
        this.callAfter(this.uiProperties.jokerWinDelay, this.updateTotalWin);
    }
    handleSlingo() {
        let numberOfSlingos = this.model.getSlingosAwarded();
        this.slingoAnimation.play(numberOfSlingos);
        this.slingoAnimation.visible = true;
    }
    handleSlingoAnimationComplete() {
        this.slingoAnimation.visible = false;
        if (this.factory.useFlowStateMachine?.()) {
            this.dispatchGame(SlingoEvent.SLINGO_WIN_ANIMATION_COMPLETE);
        }
        else if (this.model.isExpressGame()) {
            this.dispatchGame(SlingoEvent.SLINGO_PAY_TABLE);
        }
    }
    handleStart() {
        let resultData = this.model.getResultData();
        resultData.hasNoWin() ? this.startGameOver() : this.startSummary();
        this.updateTotalWin();
    }
    startSummary() {
        let endY = this.summary.y;
        this.summary.y = this.summary.y + this.summary.height;
        this.summary.scale.x = this.uiProperties.gameSummaryScale.x;
        this.summary.scale.y = this.uiProperties.gameSummaryScale.y;
        this.summary.update();
        gsap.to(this.summary, {
            pixi: {
                scaleX: this.summary.getProperties().scale.x,
                scaleY: this.summary.getProperties().scale.y
            },
            y: endY,
            ease: Back.easeOut,
            duration: 0.4,
            onComplete: this.handleSummaryTweenComplete,
            callbackScope: this
        });
        gsap.to(this.summary.scale, {
            x: this.summary.getProperties().scale.x,
            y: this.summary.getProperties().scale.y,
            ease: Back.easeInOut,
            duration: 0.4,
            callbackScope: this
        });
        this.addChild(this.summary);
        this.dispatchGame(SlingoEvent.GAME_SUMMARY);
    }
    startGameOver() {
        this.dispatchGame(SlingoEvent.GAME_SUMMARY);
        this.gameOverField.visible = false;
        let endY = this.gameOver.y;
        this.gameOver.y = this.gameOver.y + this.gameOver.height;
        this.gameOver.scale.x = this.uiProperties.gameSummaryScale.x;
        this.gameOver.scale.y = this.uiProperties.gameSummaryScale.y;
        gsap.to(this.gameOver, {
            pixi: { scale: 1 },
            y: endY,
            ease: Back.easeOut,
            duration: 0.4,
            onComplete: this.handleGameOverTweenComplete,
            callbackScope: this
        });
        this.gameOverContainer.addChild(this.gameOver);
    }
    handleSummaryTweenComplete() {
        this.summary.play();
    }
    handleGameOverTweenComplete() {
        this.gameOverField.visible = true;
        this.callAfter(1500, this.handleGameComplete);
    }
    handleGameComplete() {
        this.gameOverContainer.removeChild(this.gameOver);
        this.removeChild(this.summary);
        this.dispatchGame(SlingoEvent.WIN_DISPLAY_COMPLETE);
        this.dispatchGame(CommonGameEvent.COMPLETE);
    }
    handleReelsComplete() {
        const result = this.model.getSpinResult();
        if (result.hasSymbolWin() && result.getSymbolWins()[0].getNumberOfSymbols() >= 3) {
            this.dispatchGame(SlingoEvent.SCATTER_WIN_ANIMATION);
        }
    }
    clearFields() {
        if (this.winField) {
            this.winField.clear();
        }
    }
    updateTotalWin() {
        if (this.winField) {
            this.winField.setValue(this.model.getTotalWin());
        }
    }
    /* Test Methods */
    handleTestSlingo1() {
        this.handleTestSlingo(1);
    }
    handleTestSlingo2() {
        this.handleTestSlingo(2);
    }
    handleTestSlingo3() {
        this.handleTestSlingo(3);
    }
    handleTestSlingo4() {
        this.handleTestSlingo(4);
    }
    handleTestSlingo(value) {
        this.slingoAnimation.visible = true;
        this.slingoAnimation.play(value);
    }
}
//# sourceMappingURL=WinDisplay.js.map